import { Extension } from "@tiptap/core";

const DIR_TYPES = [
  "paragraph",
  "heading",
  "blockquote",
  "bulletList",
  "orderedList",
  "listItem",
  "taskList",
  "taskItem",
  "codeBlock",
];

const INDENT_TYPES = ["paragraph", "heading"];
const MAX_INDENT = 8;

function clampIndent(value) {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(MAX_INDENT, Math.round(value));
}

function parseIndent(element) {
  const data = element.getAttribute("data-indent");
  if (data != null && data !== "") {
    const parsed = parseInt(data, 10);
    if (Number.isFinite(parsed)) return clampIndent(parsed);
  }

  const cls = element.getAttribute("class") || "";
  const ql = cls.match(/(?:^|\s)ql-indent-(\d+)(?:\s|$)/);
  if (ql) return clampIndent(parseInt(ql[1], 10));

  const style = element.getAttribute("style") || "";
  const match = style.match(
    /(?:padding|margin)-(?:inline-start|left)\s*:\s*(-?[\d.]+)\s*(px|em|rem)/i,
  );
  if (!match) return 0;
  const amount = parseFloat(match[1]);
  if (match[2] === "px") return clampIndent(Math.round(amount / 40));
  return clampIndent(Math.round(amount / 3));
}

function isInsideList($pos) {
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const name = $pos.node(depth).type.name;
    if (name === "listItem" || name === "taskItem") return true;
  }
  return false;
}

function bumpIndentNode(tr, node, pos, delta) {
  const current = Number(node.attrs.indent) || 0;
  const next = clampIndent(current + delta);
  if (next === current) return false;
  tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
  return true;
}

function applyIndentDelta({ state, dispatch }, delta) {
  const { from, to } = state.selection;
  const tr = state.tr;
  let changed = false;

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name !== "paragraph" && node.type.name !== "heading") {
      return true;
    }
    if (isInsideList(state.doc.resolve(pos))) return true;
    changed = bumpIndentNode(tr, node, pos, delta) || changed;
    return true;
  });

  if (changed && dispatch) dispatch(tr);
  return changed;
}

function inTable(editor) {
  return (
    editor.isActive("table") ||
    editor.isActive("tableCell") ||
    editor.isActive("tableHeader")
  );
}

function inList(editor) {
  return editor.isActive("listItem") || editor.isActive("taskItem");
}

export const OptionalDir = Extension.create({
  name: "optionalDir",

  addGlobalAttributes() {
    return [
      {
        types: DIR_TYPES,
        attributes: {
          dir: {
            default: null,
            parseHTML: (element) => {
              const value = element.getAttribute("dir");
              return value === "rtl" || value === "ltr" || value === "auto"
                ? value
                : null;
            },
            renderHTML: (attributes) =>
              attributes.dir ? { dir: attributes.dir } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setTextDirection:
        (direction) =>
        ({ commands, editor }) => {
          const active = DIR_TYPES.find((type) => editor.isActive(type));
          if (!active) return false;
          return commands.updateAttributes(active, { dir: direction });
        },
    };
  },
});

export const Indent = Extension.create({
  name: "indent",

  addGlobalAttributes() {
    return [
      {
        types: INDENT_TYPES,
        attributes: {
          indent: {
            default: 0,
            keepOnSplit: false,
            parseHTML: parseIndent,
            renderHTML: (attributes) => {
              const indent = Number(attributes.indent) || 0;
              if (indent < 1) return {};
              return { "data-indent": String(indent) };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ editor, commands, state, dispatch }) => {
          if (editor.isActive("codeBlock")) return false;
          if (editor.isActive("taskItem") && commands.sinkListItem("taskItem")) {
            return true;
          }
          if (editor.isActive("listItem") && commands.sinkListItem("listItem")) {
            return true;
          }
          if (inList(editor)) return false;
          return applyIndentDelta({ state, dispatch }, 1);
        },
      outdent:
        () =>
        ({ editor, commands, state, dispatch }) => {
          if (editor.isActive("codeBlock")) return false;
          if (editor.isActive("taskItem") && commands.liftListItem("taskItem")) {
            return true;
          }
          if (editor.isActive("listItem") && commands.liftListItem("listItem")) {
            return true;
          }
          if (inList(editor)) return false;
          return applyIndentDelta({ state, dispatch }, -1);
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (inTable(this.editor) || this.editor.isActive("codeBlock")) {
          return false;
        }
        // Lists only — keep Tab free for form navigation on plain text.
        if (!inList(this.editor)) return false;
        return this.editor.commands.indent();
      },
      "Shift-Tab": () => {
        if (inTable(this.editor) || this.editor.isActive("codeBlock")) {
          return false;
        }
        // List un-nest, or reduce paragraph padding; else allow focus move.
        return this.editor.commands.outdent();
      },
    };
  },
});
